import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { IonicModule, ModalController, PopoverController, AlertController } from '@ionic/angular';
import { LucideAngularModule } from 'lucide-angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { IconPickerComponent } from 'src/app/components/icon-picker/icon-picker.component';
import { ICON_MAP, getIconComponent } from 'src/constants/icon-map.constants';
import { KnowledgeBaseCollection } from 'src/models/knowledge-base-collection.model';
import { Portal } from 'src/models/portals';
import { AuthService } from 'src/services/auth.service';
import { KnowledgeBaseCollectionsService } from 'src/services/knowledge-base-collections.service';
import { MessageService } from 'src/services/message.service';
import { PortalsService } from 'src/services/portals.service';

@Component({
  selector: 'app-upsert-collection-modal',
  templateUrl: './upsert-collection-modal.component.html',
  styleUrls: ['./upsert-collection-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, FormsModule, LucideAngularModule, NgxTippyModule],
})
export class UpsertCollectionModalComponent implements OnInit {
  // Mode determines whether we're creating or editing
  isEditMode = false;
  collection: KnowledgeBaseCollection | null = null;

  collectionForm: FormGroup;
  isLoading = false;
  selectedIcon: string = '';
  includeInPortal: boolean = false;
  hasLivePortals: boolean = false;
  livePortals: Portal[] = [];
  portalsWithoutExplicitCollections: Portal[] = [];
  selectedPortals: { [portalId: string]: boolean } = {};

  // Use shared icon map
  iconMap = ICON_MAP;

  constructor(
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private formBuilder: FormBuilder,
    private collectionsService: KnowledgeBaseCollectionsService,
    private messageService: MessageService,
    private authService: AuthService,
    private portalsService: PortalsService,
  ) {
    this.collectionForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(1)]],
      description: [''],
    });
  }

  async ngOnInit() {
    // Determine if we're in edit mode based on whether collection is provided
    this.isEditMode = !!this.collection;

    // Initialize form with collection data if in edit mode
    if (this.isEditMode && this.collection) {
      this.collectionForm.patchValue({
        name: this.collection.name,
        description: this.collection.description || '',
      });
      this.selectedIcon = this.collection.icon || '';
    }

    // Check for live portals only when creating a new collection
    if (!this.isEditMode) {
      await this.checkLivePortals();
    }
  }

  // Get the icon component for a given icon name
  getIconComponent(iconName: string) {
    return getIconComponent(iconName);
  }

  // Getter for the current icon component
  get currentIconComponent() {
    if (!this.selectedIcon) return null;
    return getIconComponent(this.selectedIcon);
  }

  async selectIcon() {
    const popover = await this.popoverCtrl.create({
      component: IconPickerComponent,
      componentProps: {
        selectedIcon: this.selectedIcon,
      },
    });

    popover.onDidDismiss().then((result) => {
      if (result.data) {
        this.selectedIcon = result.data;
      }
    });

    await popover.present();
  }

  async checkLivePortals() {
    try {
      // Get project information to determine if it's internal or external
      let projectIsExternal = null;
      if (this.projectId) {
        const project = this.authService.projects.find((p) => p.id === this.projectId);
        projectIsExternal = project?.isExternal;
      }

      // Determine which portal types to fetch based on project type
      const portalPromises = [];

      // If no project or project is internal, include internal portals
      if (projectIsExternal === null || projectIsExternal === false) {
        portalPromises.push(
          this.portalsService.list({
            where: [
              { field: 'tenantId', operator: '==', value: this.authService.tenantId },
              { field: 'isEnabled', operator: '==', value: true },
              { field: 'isExternal', operator: '==', value: false },
            ],
          }),
        );
      }

      // If no project or project is external, include external portals
      if (projectIsExternal === null || projectIsExternal === true) {
        portalPromises.push(
          this.portalsService.list({
            where: [
              { field: 'tenantId', operator: '==', value: this.authService.tenantId },
              { field: 'isEnabled', operator: '==', value: true },
              { field: 'isExternal', operator: '==', value: true },
            ],
          }),
        );
      }

      const portalResults = await Promise.all(portalPromises);

      // Combine portal results
      this.livePortals = portalResults.flatMap((result) => result.data || []) as Portal[];
      this.hasLivePortals = this.livePortals.length > 0;

      // Check if any portals don't have explicit collections set
      this.portalsWithoutExplicitCollections = this.livePortals.filter(
        (portal) => !portal.collections || portal.collections.length === 0,
      );

      // Initialize selected portals (default to checked for all portals)
      this.selectedPortals = {};
      this.livePortals.forEach((portal) => {
        this.selectedPortals[portal.id] = true;
      });
    } catch (error) {
      console.error('Failed to check live portals:', error);
      this.hasLivePortals = false;
    }
  }

  async onSubmit() {
    if (this.collectionForm.valid && !this.isLoading) {
      this.isLoading = true;
      try {
        const formData = this.collectionForm.value;

        if (this.isEditMode && this.collection) {
          // Update existing collection
          await this.collectionsService.update(this.collection.id, {
            name: formData.name.trim(),
            description: formData.description?.trim() || null,
            icon: this.selectedIcon || null,
          });
        } else {
          // Create new collection
          const newCollection = await this.collectionsService.create({
            name: formData.name.trim(),
            description: formData.description?.trim() || null,
            icon: this.selectedIcon || null,
            parentId: this.parentId,
            projectId: this.projectId,
          });

          // Handle portal inclusion based on user selections
          if (this.hasLivePortals && newCollection) {
            await this.handlePortalSelections(newCollection.id);
          }
        }

        this.modalCtrl.dismiss({ success: true });
      } catch (error) {
        console.error(`${this.isEditMode ? 'Update' : 'Create'} collection failed:`, error);
        this.messageService.showError(error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  async handlePortalSelections(collectionId: string) {
    try {
      const selectedPortalIds = Object.keys(this.selectedPortals).filter((portalId) => this.selectedPortals[portalId]);
      const unselectedPortalIds = Object.keys(this.selectedPortals).filter(
        (portalId) => !this.selectedPortals[portalId],
      );

      // Add collection to selected portals
      if (selectedPortalIds.length > 0) {
        await this.addCollectionToSelectedPortals(collectionId, selectedPortalIds);
      }

      // Exclude collection from unselected portals
      if (unselectedPortalIds.length > 0) {
        await this.excludeCollectionFromSelectedPortals(collectionId, unselectedPortalIds);
      }

      // Show success message
      const selectedPortals = this.livePortals.filter((portal) => selectedPortalIds.includes(portal.id));
      const portalTypes = this.getPortalTypesDescription(selectedPortals);
      this.messageService.show(
        `Collection created and will be visible in ${selectedPortalIds.length} portal(s) (${portalTypes})`,
      );
    } catch (error) {
      console.error('Failed to handle portal selections:', error);
      this.messageService.showError('Collection created but failed to update portal settings');
    }
  }

  async handlePortalsWithoutExplicitCollections(collectionId: string) {
    try {
      // Show confirmation dialog
      const confirmed = await this.showPortalInclusionConfirmation();

      if (confirmed) {
        // User confirmed - collection will show in portals by default (no action needed)
        const portalTypes = this.getPortalTypesDescription();
        this.messageService.show(
          `Collection created and will be visible in ${this.portalsWithoutExplicitCollections.length} portal(s) (${portalTypes})`,
        );
      } else {
        // User declined - set explicit collections for all portals to exclude this collection
        await this.excludeCollectionFromPortals(collectionId);
      }
    } catch (error) {
      console.error('Failed to handle portal inclusion:', error);
      this.messageService.showError('Collection created but portal settings may not be updated');
    }
  }

  async showPortalInclusionConfirmation(): Promise<boolean> {
    return new Promise(async (resolve) => {
      const portalTypes = this.getPortalTypesDescription();
      const alert = await this.alertCtrl.create({
        header: 'Portal Visibility',
        message: `This collection will be visible in ${this.portalsWithoutExplicitCollections.length} live portal(s) by default (${portalTypes}). Do you want to keep this behavior?`,
        buttons: [
          {
            text: 'No, hide from portals',
            role: 'cancel',
            handler: () => resolve(false),
          },
          {
            text: 'Yes, show in portals',
            handler: () => resolve(true),
          },
        ],
      });

      await alert.present();
    });
  }

  getPortalTypesDescription(portals: Portal[] = this.portalsWithoutExplicitCollections): string {
    const internalPortals = portals.filter((portal) => !portal.isExternal);
    const externalPortals = portals.filter((portal) => portal.isExternal);

    const types = [];
    if (internalPortals.length > 0) {
      types.push(`${internalPortals.length} internal`);
    }
    if (externalPortals.length > 0) {
      types.push(`${externalPortals.length} external`);
    }

    return types.join(', ');
  }

  async excludeCollectionFromPortals(collectionId: string) {
    try {
      // For shared collections (projectId: null), we need to exclude from ALL live portals
      // For project-specific collections, we only exclude from portals that don't have explicit collections
      const isSharedCollection = this.projectId === null;
      const portalsToUpdate = isSharedCollection ? this.livePortals : this.portalsWithoutExplicitCollections;

      // Get all existing collections for each portal to create explicit collections list
      for (const portal of portalsToUpdate) {
        // Get all collections that would be relevant to this portal
        const relevantCollections = await this.getRelevantCollectionsForPortal(portal);

        // Filter out the newly created collection
        const collectionsWithoutNew = relevantCollections.filter((id) => id !== collectionId);

        // Update portal with explicit collections list (excluding the new collection)
        await this.portalsService.update(portal.id, {
          collections: collectionsWithoutNew,
        });
      }

      const portalTypes = this.getPortalTypesDescription(portalsToUpdate);
      this.messageService.show(
        `Collection created and hidden from ${portalsToUpdate.length} portal(s) (${portalTypes})`,
      );
    } catch (error) {
      console.error('Failed to exclude collection from portals:', error);
      this.messageService.showError('Collection created but failed to update portal settings');
    }
  }

  async getRelevantCollectionsForPortal(portal: Portal): Promise<string[]> {
    try {
      // Get collections that are relevant to this portal
      // This includes:
      // 1. Collections with projectId matching any of the portal's projectIds
      // 2. Shared collections (projectId=null) - these are available to ALL portals
      // 3. Only ROOT collections (parentId=null) - child collections are managed by their parents
      const collections = await this.collectionsService.list({
        limit: 1000,
        where: [
          {
            field: 'projectId',
            operator: 'in',
            value: [...portal.projectIds, null], // Include shared collections (projectId=null)
          },
          {
            field: 'parentId',
            operator: 'is',
            value: null, // Only include root collections
          },
        ],
      });

      return collections.data.map((collection) => collection.id);
    } catch (error) {
      console.error('Failed to get relevant collections for portal:', error);
      return [];
    }
  }

  async addCollectionToPortals(collectionId: string) {
    try {
      // Add collection to all live portals
      for (const portal of this.livePortals) {
        const updatedCollections = [...(portal.collections || []), collectionId];
        await this.portalsService.update(portal.id, {
          collections: updatedCollections,
        });
      }

      this.messageService.show(`Collection added to ${this.livePortals.length} portal(s)`);
    } catch (error) {
      console.error('Failed to add collection to portals:', error);
      this.messageService.showError('Collection created but failed to add to portals');
    }
  }

  async addCollectionToSelectedPortals(collectionId: string, portalIds: string[]) {
    try {
      for (const portalId of portalIds) {
        const portal = this.livePortals.find((p) => p.id === portalId);
        if (portal) {
          // If portal.collections is empty or null, it means "include all collections by default"
          // So we don't need to add this collection to the array - it will be automatically included
          if (portal.collections && portal.collections.length > 0) {
            const updatedCollections = [...portal.collections, collectionId];

            await this.portalsService.update(portalId, {
              collections: updatedCollections,
            });
          }
          // If portal.collections is empty/null, we don't update it - collection will be included by default
        }
      }
    } catch (error) {
      console.error('Failed to add collection to selected portals:', error);
      throw error;
    }
  }

  async excludeCollectionFromSelectedPortals(collectionId: string, portalIds: string[]) {
    try {
      for (const portalId of portalIds) {
        const portal = this.livePortals.find((p) => p.id === portalId);
        if (portal) {
          const relevantCollections = await this.getRelevantCollectionsForPortal(portal);
          const collectionsWithoutNew = relevantCollections.filter((id) => id !== collectionId);
          await this.portalsService.update(portalId, {
            collections: collectionsWithoutNew,
          });
        }
      }
    } catch (error) {
      console.error('Failed to exclude collection from selected portals:', error);
      throw error;
    }
  }

  onCancel() {
    this.modalCtrl.dismiss({ success: false });
  }

  // These will be set by the parent component via componentProps
  parentId: string | null = null;
  projectId: string | null = null;
}
